import { useEffect, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { useAuth } from '@/contexts/auth';
import { getAccessRole } from '@/utils/rbac';
import { getBulkInternalRolesForAccess } from '../constants';
import type {
  BulkPreviewData,
  BulkUserType,
  EditableBulkRow,
  InternalPreviewData,
  PartnerPreviewData,
} from '../types';
import { getFieldError, validateRowByType } from '../utils';

interface BulkImportEditRowProps {
  row: EditableBulkRow | null;
  userType: BulkUserType;
  onClose: () => void;
  onSave: (_rowId: string, _data: BulkPreviewData) => void;
}

export function BulkImportEditRow({ row, userType, onClose, onSave }: BulkImportEditRowProps) {
  const { user: currentUser } = useAuth();
  const accessRole = getAccessRole(currentUser);
  const internalRoles = getBulkInternalRolesForAccess(accessRole);
  const [nome, setNome] = useState('');
  const [email, setEmail] = useState('');
  const [role, setRole] = useState('');
  const [whatsapp, setWhatsapp] = useState('');

  useEffect(() => {
    if (!row) return;
    setNome(row.data.nome ?? '');
    setEmail(row.data.email ?? '');
    if ('role' in row.data) {
      setRole(row.data.role ?? '');
    }
    if ('whatsapp' in row.data) {
      setWhatsapp(row.data.whatsapp ?? '');
    }
  }, [row]);

  if (!row) return null;

  const draftData: BulkPreviewData =
    userType === 'internal'
      ? { nome, email, role }
      : { nome, email, whatsapp };

  const localErrors = validateRowByType(userType, draftData, accessRole);
  const errorByField = {
    nome: localErrors.find((error) => error.field === 'nome')?.message ?? getFieldError(row, 'nome'),
    email: localErrors.find((error) => error.field === 'email')?.message ?? getFieldError(row, 'email'),
    role: localErrors.find((error) => error.field === 'role')?.message ?? getFieldError(row, 'role'),
    whatsapp:
      localErrors.find((error) => error.field === 'whatsapp')?.message ?? getFieldError(row, 'whatsapp'),
  };

  const handleSave = () => {
    if (localErrors.length > 0) return;
    onSave(row.id, draftData);
  };

  return (
    <Dialog open={!!row} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-[480px] w-[calc(100%-2rem)]">
        <DialogHeader>
          <DialogTitle>Editar linha {row.rowNumber}</DialogTitle>
          <DialogDescription>
            Ajuste os dados antes da confirmação. O backend fará uma nova validação completa.
          </DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="space-y-2">
            <label htmlFor="bulk-edit-nome" className="text-sm font-medium text-gray-700">
              Nome
            </label>
            <Input
              id="bulk-edit-nome"
              value={nome}
              onChange={(event) => setNome(event.target.value)}
              error={errorByField.nome}
              className={errorByField.nome ? 'border-red-500' : ''}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor="bulk-edit-email" className="text-sm font-medium text-gray-700">
              E-mail
            </label>
            <Input
              id="bulk-edit-email"
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              error={errorByField.email}
              className={errorByField.email ? 'border-red-500' : ''}
            />
          </div>
          {userType === 'internal' ? (
            <div className="space-y-2">
              <label htmlFor="bulk-edit-role" className="text-sm font-medium text-gray-700">
                Role
              </label>
              <Select value={(draftData as InternalPreviewData).role} onValueChange={setRole}>
                <SelectTrigger id="bulk-edit-role" className={errorByField.role ? 'border-red-500' : ''}>
                  <SelectValue placeholder="Selecione a role" />
                </SelectTrigger>
                <SelectContent>
                  {internalRoles.map((item) => (
                    <SelectItem key={item.value} value={item.value}>
                      {item.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
              {errorByField.role && (
                <p className="text-xs text-red-500" role="alert">
                  {errorByField.role}
                </p>
              )}
            </div>
          ) : (
            <div className="space-y-2">
              <label htmlFor="bulk-edit-whatsapp" className="text-sm font-medium text-gray-700">
                WhatsApp
              </label>
              <Input
                id="bulk-edit-whatsapp"
                value={(draftData as PartnerPreviewData).whatsapp}
                onChange={(event) => setWhatsapp(event.target.value)}
                error={errorByField.whatsapp}
                className={errorByField.whatsapp ? 'border-red-500' : ''}
              />
            </div>
          )}
        </div>
        <DialogFooter className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose}>
            Cancelar
          </Button>
          <Button type="button" onClick={handleSave} disabled={localErrors.length > 0}>
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
